import { Button, IconButton, List, ListItem, ListItemText, TextField, Tooltip, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { generalInformationStepFormSchema } from "validations/schemas";
import { useStyles } from "./GeneralInformationStep.styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
    IGeneralInformationStep,
    IGeneralInformationStepFormValues,
    IRegionOnboardingFormBO,
} from "./GeneralInformationStepInterfaces";
import { MuiChipsInput } from "mui-chips-input";
import { useCallback, useMemo, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { USERTYPE } from "constants/constants";
import { ICorporationCorporateBO } from "entities/Corporations/CorporationsEntityInterfaces";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";

const GeneralInformationStep = ({ corporation, onFinish, onlyGeneralInformationStep }: IGeneralInformationStep) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [editDivisionField, setEditDivisionField] = useState<number | null>(null);
    const administratorUsers = useMemo(
        () => (corporation.users || []).filter((user) => user.user_type.user_type_id === USERTYPE.ADMINISTRATOR),
        [corporation.users],
    );
    const { upsertCorporateData, apolloMutationError } = CorporationsEntity.UpsertCorporateDataOnboardingBO();

    const initFormValues: IGeneralInformationStepFormValues = useMemo(
        () => ({
            holdingCompany: corporation?.corporate_data?.holding_company?.name || "",
            holdingCompanyAddress: corporation?.corporate_data?.holding_company?.address || "",
            holdingCompanyWebsite: corporation?.corporate_data?.holding_company?.url || "",
            corporateName: corporation.name || "",
            corporateAddress: corporation.address_1 || "",
            corporateWebsite: corporation?.corporate_data?.url || "",
            corporatePhone: corporation.phone_number || "",
            divisionInput: "",
            divisionEditInput: "",
            divisions: corporation.divisions || [],
        }),
        [corporation],
    );

    const handleSubmit = useCallback(
        async (values: IGeneralInformationStepFormValues) => {
            const corporateChangesValues = {
                ...(corporation.corporate_id && { corporate_id: corporation.corporate_id }),
                ...(values.corporateName && { name: values.corporateName }),
                ...(values.corporateAddress && { address_1: values.corporateAddress }),
                ...(values.corporatePhone && { phone_number: values.corporatePhone }),
            };
            const corporateDataValues = {
                ...(corporation?.corporate_data?.corporate_data_id && {
                    corporate_data_id: corporation.corporate_data.corporate_data_id,
                }),
                ...(corporation?.corporate_id && { corporate_id: corporation.corporate_id }),
                ...(values.corporateWebsite && { url: values.corporateWebsite }),
                holding_company: {
                    data: {
                        ...(corporation?.corporate_data?.holding_company?.holding_company_id && {
                            holding_company_id: corporation.corporate_data.holding_company.holding_company_id,
                        }),
                        ...(corporation.corporate_id && { corporate_id: corporation.corporate_id }),
                        ...(values.holdingCompany && { name: values.holdingCompany }),
                        ...(values.holdingCompanyAddress && { address: values.holdingCompanyAddress }),
                        ...(values.holdingCompanyWebsite && { url: values.holdingCompanyWebsite }),
                    },
                    on_conflict: { constraint: "holding_company_pkey", update_columns: ["name", "address", "url"] },
                },
            };

            let deepClonedDivisions: ICorporationCorporateBO["divisions"] = JSON.parse(
                JSON.stringify(values.divisions),
            );
            const divisionValues = deepClonedDivisions.map((division) => {
                delete division.__typename;
                return {
                    ...division,
                    corporate_id: corporation.corporate_id,
                    regions: {
                        data: division.regions.map((region) => {
                            delete region.__typename;
                            delete region.mills;
                            return { ...region };
                        }),
                        on_conflict: {
                            constraint: "region_pkey",
                            update_columns: ["name", "deleted_at"],
                        },
                    },
                };
            });

            try {
                const response = await upsertCorporateData({
                    variables: {
                        corporate_id: corporation.corporate_id,
                        corporateChanges: corporateChangesValues,
                        corporate_data: corporateDataValues,
                        division: divisionValues,
                    },
                });
                if (response) {
                    await onFinish();
                }
            } catch {
                return;
            }
        },
        [upsertCorporateData, onFinish, corporation],
    );

    return (
        <div>
            <div className={classes.titleContainer}>
                <h1 className={classes.formTitle}>{t("general_information.title")}</h1>
            </div>
            <Formik
                initialValues={initFormValues}
                validationSchema={generalInformationStepFormSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                    return (
                        <Form className={classes.fieldsContainer}>
                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>{t("general_information.holding_company")}</h3>
                            </div>
                            <TextField
                                fullWidth
                                id="outlined-holdingCompany-input"
                                name="holdingCompany"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                autoFocus
                                label={t("general_information.holding_company")}
                                value={values.holdingCompany}
                                onChange={handleChange}
                                error={touched.holdingCompany && Boolean(errors.holdingCompany)}
                                helperText={touched.holdingCompany && errors.holdingCompany && t(errors.holdingCompany)}
                            />
                            <TextField
                                fullWidth
                                id="outlined-holdingCompanyAddress-input"
                                name="holdingCompanyAddress"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.holding_company_address")}
                                value={values.holdingCompanyAddress}
                                onChange={handleChange}
                                error={touched.holdingCompanyAddress && Boolean(errors.holdingCompanyAddress)}
                                helperText={
                                    touched.holdingCompanyAddress &&
                                    errors.holdingCompanyAddress &&
                                    t(errors.holdingCompanyAddress)
                                }
                            />
                            <TextField
                                fullWidth
                                id="outlined-holdingCompanyWebsite-input"
                                name="holdingCompanyWebsite"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.holding_company_website")}
                                value={values.holdingCompanyWebsite}
                                onChange={handleChange}
                                error={touched.holdingCompanyWebsite && Boolean(errors.holdingCompanyWebsite)}
                                helperText={
                                    touched.holdingCompanyWebsite &&
                                    errors.holdingCompanyWebsite &&
                                    t(errors.holdingCompanyWebsite)
                                }
                            />

                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>{t("general_information.corporate")}</h3>
                            </div>

                            <TextField
                                fullWidth
                                id="outlined-corporateName-input"
                                name="corporateName"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.corporate_name")}
                                value={values.corporateName}
                                onChange={handleChange}
                                error={touched.corporateName && Boolean(errors.corporateName)}
                                helperText={touched.corporateName && errors.corporateName && t(errors.corporateName)}
                            />

                            <TextField
                                fullWidth
                                id="outlined-corporateAddress-input"
                                name="corporateAddress"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.corporate_address")}
                                value={values.corporateAddress}
                                onChange={handleChange}
                                error={touched.corporateAddress && Boolean(errors.corporateAddress)}
                                helperText={
                                    touched.corporateAddress && errors.corporateAddress && t(errors.corporateAddress)
                                }
                            />

                            <TextField
                                fullWidth
                                id="outlined-corporateWebsite-input"
                                name="corporateWebsite"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.corporate_website")}
                                value={values.corporateWebsite}
                                onChange={handleChange}
                                error={touched.corporateWebsite && Boolean(errors.corporateWebsite)}
                                helperText={
                                    touched.corporateWebsite && errors.corporateWebsite && t(errors.corporateWebsite)
                                }
                            />

                            <TextField
                                fullWidth
                                id="outlined-corporatePhone-input"
                                name="corporatePhone"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.corporate_phone_number")}
                                value={values.corporatePhone}
                                onChange={handleChange}
                                error={touched.corporatePhone && Boolean(errors.corporatePhone)}
                                helperText={touched.corporatePhone && errors.corporatePhone && t(errors.corporatePhone)}
                            />
                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>{t("general_information.divisions")}</h3>
                            </div>

                            <TextField
                                fullWidth
                                id="outlined-division-input"
                                name="divisionInput"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("general_information.add_division")}
                                error={
                                    touched.divisions &&
                                    typeof errors.divisions === "string" &&
                                    Boolean(errors.divisions)
                                }
                                helperText={
                                    touched.divisions &&
                                    errors.divisions &&
                                    typeof errors.divisions === "string" &&
                                    t(errors.divisions.toString())
                                }
                                value={values.divisionInput}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        if (Boolean(values.divisionInput?.trim())) {
                                            const duplicatedValues = values.divisions.findIndex((division) => {
                                                return (
                                                    division.name.toLowerCase() ===
                                                    values.divisionInput.trim().toLowerCase()
                                                );
                                            });

                                            if (duplicatedValues !== -1) {
                                                setFieldValue("divisionInput", "");
                                                return;
                                            }

                                            setFieldValue("divisions", [
                                                ...values.divisions,
                                                {
                                                    name: values.divisionInput,
                                                    regions: [],
                                                },
                                            ]);

                                            setFieldValue("divisionInput", "");
                                        }
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => {
                                                if (Boolean(values.divisionInput?.trim())) {
                                                    const duplicatedValues = values.divisions.findIndex((division) => {
                                                        return (
                                                            division.name.toLowerCase() ===
                                                            values.divisionInput.trim().toLowerCase()
                                                        );
                                                    });

                                                    if (duplicatedValues !== -1) {
                                                        setFieldValue("divisionInput", "");
                                                        return;
                                                    }

                                                    setFieldValue("divisions", [
                                                        ...values.divisions,
                                                        {
                                                            name: values.divisionInput,
                                                            regions: [],
                                                        },
                                                    ]);

                                                    setFieldValue("divisionInput", "");
                                                }
                                            }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    ),
                                }}
                                onChange={handleChange}
                            />
                            {Boolean(values.divisions.length) && (
                                <List className={classes.listContainer}>
                                    {values.divisions
                                        .filter((division) => !division.deleted_at)
                                        .map((division, index) => {
                                            const regions: IRegionOnboardingFormBO[] = division.regions;
                                            const divisionWithMill =
                                                regions.filter((region) =>
                                                    region.mills ? region.mills.length > 0 : false,
                                                ).length > 0;

                                            const divisionIndex = values.divisions.findIndex(
                                                (div) => division.name === div.name,
                                            );

                                            const handleChangeRegions = (newRegions: string[]) => {
                                                let deepClonedDivisions: ICorporationCorporateBO["divisions"] =
                                                    JSON.parse(JSON.stringify(values.divisions));

                                                const toFindDuplicates = (array: string[]) =>
                                                    array.filter(
                                                        (item, index) =>
                                                            array
                                                                .map((element) => element.toLowerCase())
                                                                .indexOf(item.toLowerCase()) !== index,
                                                    );
                                                const duplicateElements = toFindDuplicates(newRegions);
                                                if (duplicateElements.length) return;

                                                deepClonedDivisions[divisionIndex] = {
                                                    ...deepClonedDivisions[divisionIndex],
                                                    regions: newRegions.map((newRegion, index) => {
                                                        const existingIndex =
                                                            deepClonedDivisions[divisionIndex].regions[index];
                                                        return existingIndex
                                                            ? {
                                                                  ...deepClonedDivisions[divisionIndex].regions[index],
                                                                  name: newRegion,
                                                              }
                                                            : { name: newRegion };
                                                    }),
                                                };
                                                setFieldValue("divisions", deepClonedDivisions);
                                            };

                                            return (
                                                <div key={division.name} className={classes.separator}>
                                                    <ListItem className={classes.listItem}>
                                                        {editDivisionField !== index && (
                                                            <ListItemText
                                                                className={classes.listText}
                                                                primary={division.name}
                                                            />
                                                        )}

                                                        {editDivisionField === index && (
                                                            <TextField
                                                                fullWidth
                                                                label={t("general_information.edit_name_division")}
                                                                variant="outlined"
                                                                name="divisionEditInput"
                                                                onChange={handleChange}
                                                                value={values.divisionEditInput}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === "Enter") {
                                                                        event.preventDefault();

                                                                        if (Boolean(values.divisionEditInput?.trim())) {
                                                                            const divisions = [...values.divisions];
                                                                            divisions[divisionIndex] = {
                                                                                ...divisions[divisionIndex],
                                                                                name: values.divisionEditInput,
                                                                            };

                                                                            setFieldValue("divisions", divisions);
                                                                            setEditDivisionField(null);
                                                                        }
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <>
                                                                            <IconButton
                                                                                onClick={() => {
                                                                                    if (
                                                                                        Boolean(
                                                                                            values.divisionEditInput?.trim(),
                                                                                        )
                                                                                    ) {
                                                                                        const divisions = [
                                                                                            ...values.divisions,
                                                                                        ];
                                                                                        divisions[divisionIndex] = {
                                                                                            ...divisions[divisionIndex],
                                                                                            name: values.divisionEditInput,
                                                                                        };

                                                                                        setFieldValue(
                                                                                            "divisions",
                                                                                            divisions,
                                                                                        );
                                                                                        setEditDivisionField(null);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <CheckIcon />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    setEditDivisionField(null)
                                                                                }
                                                                            >
                                                                                <CloseIcon />
                                                                            </IconButton>
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        {editDivisionField !== index && (
                                                            <>
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setFieldValue(
                                                                            "divisionEditInput",
                                                                            division.name,
                                                                        );
                                                                        setEditDivisionField(index);
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                                {divisionWithMill ? (
                                                                    <Tooltip
                                                                        PopperProps={{
                                                                            className: classes.tooltip,
                                                                        }}
                                                                        placement="top"
                                                                        title={
                                                                            <Typography
                                                                                className={classes.tooltipTexts}
                                                                            >
                                                                                {t(
                                                                                    "general_information.no_deletion_division_note",
                                                                                )}
                                                                            </Typography>
                                                                        }
                                                                    >
                                                                        <div>
                                                                            <IconButton disabled={true}>
                                                                                <DeleteIcon />
                                                                            </IconButton>
                                                                        </div>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <IconButton
                                                                        onClick={() => {
                                                                            if (regions.length > 0) {
                                                                                setFieldValue(
                                                                                    "divisions",
                                                                                    values.divisions
                                                                                        .map((divisionDelete) => {
                                                                                            if (
                                                                                                divisionDelete.name ===
                                                                                                    division.name &&
                                                                                                divisionDelete.division_id
                                                                                            ) {
                                                                                                return {
                                                                                                    ...divisionDelete,
                                                                                                    deleted_at:
                                                                                                        new Date(),
                                                                                                    regions:
                                                                                                        divisionDelete.regions
                                                                                                            .map(
                                                                                                                (
                                                                                                                    region,
                                                                                                                ) => {
                                                                                                                    if (
                                                                                                                        region.region_id
                                                                                                                    ) {
                                                                                                                        return {
                                                                                                                            ...region,
                                                                                                                            deleted_at:
                                                                                                                                new Date(),
                                                                                                                        };
                                                                                                                    } else
                                                                                                                        return region;
                                                                                                                },
                                                                                                            )
                                                                                                            .filter(
                                                                                                                (
                                                                                                                    region,
                                                                                                                ) =>
                                                                                                                    region?.region_id,
                                                                                                            ),
                                                                                                };
                                                                                            } else
                                                                                                return divisionDelete;
                                                                                        })
                                                                                        .filter((divisionDelete) =>
                                                                                            divisionDelete.division_id
                                                                                                ? true
                                                                                                : divisionDelete.name !==
                                                                                                  division.name,
                                                                                        ),
                                                                                );
                                                                            } else {
                                                                                setFieldValue(
                                                                                    "divisions",
                                                                                    values.divisions
                                                                                        .map((divisionDelete) => {
                                                                                            if (
                                                                                                divisionDelete.name ===
                                                                                                    division.name &&
                                                                                                divisionDelete.division_id
                                                                                            ) {
                                                                                                return {
                                                                                                    ...divisionDelete,
                                                                                                    deleted_at:
                                                                                                        new Date(),
                                                                                                };
                                                                                            } else
                                                                                                return divisionDelete;
                                                                                        })
                                                                                        .filter((divisionDelete) =>
                                                                                            divisionDelete.division_id
                                                                                                ? true
                                                                                                : divisionDelete.name !==
                                                                                                  division.name,
                                                                                        ),
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                )}
                                                            </>
                                                        )}
                                                    </ListItem>
                                                    <MuiChipsInput
                                                        value={regions.map((region) => region.name)}
                                                        onChange={handleChangeRegions}
                                                        className={classes.chips}
                                                        error={Boolean(
                                                            touched.divisions &&
                                                                errors.divisions?.length &&
                                                                errors.divisions[index],
                                                        )}
                                                        helperText={
                                                            touched.divisions &&
                                                            errors.divisions?.length &&
                                                            errors.divisions[index] &&
                                                            typeof errors.divisions == "object" &&
                                                            t("field_errors.at_least_one_item_required")
                                                        }
                                                        label={t("general_information.region")}
                                                        renderChip={(Component, key, props) => {
                                                            const regionIndex = regions.findIndex(
                                                                (region) => region.name === props.label,
                                                            );
                                                            const region = regions[regionIndex]?.mills;
                                                            const hasMill: boolean = region ? region.length > 0 : false;
                                                            const deleted = regions[regionIndex]?.deleted_at;

                                                            return (
                                                                <Component
                                                                    style={{ display: deleted ? "none" : "" }}
                                                                    {...props}
                                                                    key={key}
                                                                    deleteIcon={
                                                                        hasMill ? (
                                                                            <Tooltip
                                                                                PopperProps={{
                                                                                    className: classes.tooltip,
                                                                                }}
                                                                                placement="top"
                                                                                title={
                                                                                    <Typography
                                                                                        className={classes.tooltipTexts}
                                                                                    >
                                                                                        {t(
                                                                                            "general_information.no_deletion_region_note",
                                                                                        )}
                                                                                    </Typography>
                                                                                }
                                                                            >
                                                                                <CancelIcon />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <CancelIcon />
                                                                        )
                                                                    }
                                                                    onDelete={() => {
                                                                        let deepClonedDivisions: ICorporationCorporateBO["divisions"] =
                                                                            JSON.parse(
                                                                                JSON.stringify(values.divisions),
                                                                            );
                                                                        let deepClonedRegions: IRegionOnboardingFormBO[] =
                                                                            JSON.parse(JSON.stringify(regions));

                                                                        if (
                                                                            deepClonedRegions[regionIndex]?.mills
                                                                                ?.length
                                                                        )
                                                                            return;

                                                                        if (deepClonedRegions[regionIndex]?.region_id) {
                                                                            deepClonedRegions[regionIndex] = {
                                                                                ...deepClonedRegions[regionIndex],
                                                                                deleted_at: new Date(),
                                                                            };
                                                                            deepClonedDivisions[divisionIndex] = {
                                                                                ...deepClonedDivisions[divisionIndex],
                                                                                regions: deepClonedRegions,
                                                                            };
                                                                            setFieldValue(
                                                                                "divisions",
                                                                                deepClonedDivisions,
                                                                            );
                                                                        } else {
                                                                            deepClonedDivisions[divisionIndex] = {
                                                                                ...deepClonedDivisions[divisionIndex],
                                                                                regions: deepClonedRegions.filter(
                                                                                    (region) =>
                                                                                        region.name !==
                                                                                        deepClonedRegions[regionIndex]
                                                                                            .name,
                                                                                ),
                                                                            };
                                                                            setFieldValue(
                                                                                "divisions",
                                                                                deepClonedDivisions,
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                        disableDeleteOnBackspace
                                                        hideClearAll
                                                        placeholder={
                                                            !division.regions.length
                                                                ? t("general_information.region_placeholder")
                                                                : ""
                                                        }
                                                    />
                                                </div>
                                            );
                                        })}
                                </List>
                            )}

                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>
                                    {t("general_information.corporation_administrator")}
                                </h3>
                            </div>
                            <div className={classes.administratorContainer}>
                                {administratorUsers &&
                                    administratorUsers.length &&
                                    administratorUsers.map((administratorUser) => (
                                        <div key={administratorUser.email}>
                                            <div>{`${administratorUser?.first_name} ${administratorUser?.last_name}`}</div>
                                            <div>{administratorUser?.email}</div>
                                        </div>
                                    ))}
                            </div>

                            <p style={{ display: apolloMutationError ? "block" : "none", color: "red" }}>
                                {apolloMutationError?.message.includes("uq_corporate_name")
                                    ? t("setup_corporation.errors.existing_name")
                                    : t("setup_corporation.errors.creation_error")}
                            </p>
                            <div className={classes.titleContainer}>
                                <Button
                                    className={classes.submitButton}
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {onlyGeneralInformationStep
                                        ? t("general_information.form_submit_only_save")
                                        : t("general_information.form_submit")}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default GeneralInformationStep;
